import { matchPath } from 'react-router-dom';

export interface ScreenDefinition {
  /** Route pattern, as understood by react-router. */
  readonly path: string;
  /** Title shown in the top bar and used as the screen's `<h1>`. */
  readonly title: string;
}

/** Route of the login screen — the only screen rendered outside the shell. */
export const LOGIN_PATH = '/login';

/** Where an authenticated session lands. */
export const DEFAULT_PATH = '/dashboard';

/** Title used when no pattern matches (the catch-all screen). */
export const NOT_FOUND_TITLE = 'Page not found';

/**
 * Every screen of the Docsight sitemap (UI design flow §4). Order matters:
 * `titleForPath` returns the first match, so concrete paths precede patterns.
 */
export const SCREENS: readonly ScreenDefinition[] = [
  { path: LOGIN_PATH, title: 'Sign in to Docsight' },
  { path: '/dashboard', title: 'Dashboard' },
  { path: '/documents', title: 'Documents' },
  { path: '/documents/:documentId', title: 'Document detail' },
  // Reachable from an export-ready email; it has a route and a title but
  // deliberately no left-nav entry.
  { path: '/documents/exports/:exportId', title: 'Export download' },
  { path: '/review', title: 'Review queue' },
  { path: '/review/:reviewItemId', title: 'Review item' },
  { path: '/automation', title: 'Automation opportunities' },
  { path: '/taxonomy', title: 'Taxonomy' },
  { path: '/settings/sources', title: 'Sources & API keys' },
  { path: '/settings/desktop', title: 'Desktop app' },
  { path: '/settings/mailboxes', title: 'Mailboxes' },
  { path: '/settings/locations', title: 'Locations & scanners' },
  { path: '/settings/retention', title: 'Retention' },
  { path: '/settings/users', title: 'Users' },
  { path: '/settings/branding', title: 'Branding' },
  { path: '/settings/webhooks', title: 'Webhooks' },
  { path: '/admin/partner', title: 'Partner console' },
  { path: '/admin/document-fields', title: 'Document fields' },
  { path: '/admin/system', title: 'System admin' },
  { path: '/admin/desktop-app', title: 'Desktop app' },
  { path: '/admin/health', title: 'Processing health' },
  // Reachable by any authenticated user from the top-bar profile menu; it has a
  // route and a title but deliberately no left-nav entry.
  { path: '/profile', title: 'Profile' },
];

/** Screens that live inside the app shell. */
export const SHELL_SCREENS: readonly ScreenDefinition[] = SCREENS.filter(
  (screen) => screen.path !== LOGIN_PATH,
);

/** Resolve the title the top bar should show for a pathname. */
export function titleForPath(pathname: string): string {
  for (const screen of SCREENS) {
    if (matchPath({ path: screen.path, end: true }, pathname) !== null) {
      return screen.title;
    }
  }
  return NOT_FOUND_TITLE;
}
